import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { type Patient } from "services/types/patients.type";
import { formatPhoneNumber } from "utils/common";

import { NEON_NOW_EVENT, useNeonNow } from "../../neon-now/use-neon-now.hook";
import { normalizePhoneNumber } from "../utils";
import CallButton from "./call-button.component";

type CallPatientNumberButtonProps = {
  phoneNumber?: string;
  patient?: Partial<Patient>;
  size?: "small" | "medium";
  title?: string;
  redirect?: boolean;
  sx?: SxProps;
};
export default function CallPatientNumberButton({
  phoneNumber,
  patient,
  title,
  redirect,
  size = "medium",
  sx
}: CallPatientNumberButtonProps) {
  const { sendAction } = useNeonNow();
  const navigate = useNavigate();
  const handleMakeCall = useCallback(
    (phoneNumber: string) => {
      const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
      const payload = {
        actionId: `dial_${Date.now()}`,
        phoneNumber: `+1${normalizedPhoneNumber}`,
        allowConference: false
      };
      sendAction(NEON_NOW_EVENT.DIAL_OUTBOUND_NUMBER, payload);
    },
    [sendAction]
  );

  const handleCallButtonClick = useCallback(async () => {
    if (redirect && patient?.id) {
      navigate(`/patients/${patient.id}/`);
    }
    setTimeout(async () => {
      handleMakeCall(phoneNumber ?? patient?.phone ?? "");
    }, 1);
  }, [
    handleMakeCall,
    navigate,
    patient?.id,
    patient?.phone,
    phoneNumber,
    redirect
  ]);

  return (
    <Tooltip
      disableInteractive
      title={`Call ${title || ""} ${formatPhoneNumber(phoneNumber ?? "")}`}
    >
      <Box>
        <CallButton
          sx={{
            transform: size === "medium" ? "scale(0.75)" : "scale(0.6)",
            ...sx
          }}
          onClick={handleCallButtonClick}
        />
      </Box>
    </Tooltip>
  );
}
